import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { SubHeader } from "@/components/Brand";
import { api, errMsg, useApi } from "@/lib/api";
import { rp, simulate } from "@/lib/format";

const TENORS = [6, 12, 18, 24, 36, 48, 60];

export default function LoanApply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const simOnly = params.get("mode") === "simulasi";
  const { data: settings } = useApi("/settings");
  const [amount, setAmount] = useState(5000000);
  const [tenor, setTenor] = useState(12);
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const rate = settings?.loan_rate ?? 1.5;
  const s = simulate(amount, tenor, rate);

  const submit = async () => {
    if (purpose.trim().length < 2) return toast.error("Isi tujuan pinjaman");
    setBusy(true);
    try {
      await api.post("/loans", { amount, tenor_months: tenor, purpose });
      toast.success("Pengajuan pinjaman terkirim");
      navigate("/app/pinjaman");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SubHeader title={simOnly ? "Simulasi Kredit" : "Ajukan Pinjaman"} onBack={() => navigate("/app/pinjaman")} />
      <div className="px-5 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-800">Jumlah Pinjaman</label>
            <div className="flex items-center border border-slate-300 rounded-xl mt-1.5 px-3 focus-within:ring-2 focus-within:ring-blue-700">
              <span className="text-slate-500 text-sm">Rp</span>
              <input data-testid="loan-amount-input" inputMode="numeric" value={amount.toLocaleString("id-ID")}
                onChange={(e) => setAmount(Math.min(Number(e.target.value.replace(/\D/g, "")) || 0, 1000000000))}
                className="w-full py-3 px-2 outline-none font-bold text-lg bg-transparent" />
            </div>
            <Slider className="mt-4" min={500000} max={100000000} step={500000} value={[amount]} onValueChange={([v]) => setAmount(v)} data-testid="loan-amount-slider" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-800">Tenor (bulan)</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {TENORS.map((t) => (
                <button key={t} onClick={() => setTenor(t)} data-testid={`loan-tenor-${t}`}
                  className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${tenor === t ? "bg-blue-800 text-white border-blue-800" : "bg-white border-slate-200 hover:bg-slate-100"}`}>{t}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 text-white rounded-2xl shadow-blue-500/30 p-5 shadow-lg" data-testid="loan-simulation-result">
          <div className="text-xs text-blue-100">Estimasi Angsuran per Bulan</div>
          <div className="text-3xl font-bold font-display" data-testid="sim-installment">{rp(s.installment)}</div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div><div className="text-xs text-blue-100">Pokok / bulan</div><b>{rp(s.principal)}</b></div>
            <div><div className="text-xs text-blue-100">Bunga / bulan ({rate}%)</div><b>{rp(s.interest)}</b></div>
            <div><div className="text-xs text-blue-100">Total bunga</div><b>{rp(s.totalInterest)}</b></div>
            <div><div className="text-xs text-blue-100">Total pembayaran</div><b data-testid="sim-total">{rp(s.total)}</b></div>
          </div>
          <p className="text-[11px] text-blue-100 mt-3">*Perhitungan bunga flat. Nilai akhir mengikuti keputusan pengurus.</p>
        </div>

        {simOnly ? (
          <button onClick={() => navigate("/app/pinjaman/ajukan")} data-testid="sim-to-apply-button" className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3.5 rounded-xl transition-colors">
            Ajukan dengan Simulasi Ini
          </button>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <label className="text-sm font-semibold text-slate-800">Tujuan Pinjaman</label>
              <Textarea data-testid="loan-purpose-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Contoh: modal usaha, biaya pendidikan" className="mt-1.5" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="submit-loan-button" className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors">
              {busy ? "Mengirim..." : "Kirim Pengajuan"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
